import { ReactNode, useEffect, useState } from 'react';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import InstallPromptLocation from '@alltrails/amplitude/enums/InstallPromptLocation';
import logInstallPromptDismissed from '@alltrails/amplitude/events/logInstallPromptDismissed';
import logInstallPromptViewed from '@alltrails/amplitude/events/logInstallPromptViewed';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import { Modal } from '@alltrails/modal';
import styles from './BaseInstallInterstitial.module.scss';

type BaseInstallInterstitialProps = {
  isOpen?: boolean;
  onRequestClose?: () => void;
  promptLocation: InstallPromptLocation;
  displayTrigger?: CarouselDisplayTrigger;
  children: ReactNode;
  hidePageScrollbarGutter?: boolean;
  isDeviceHeight?: boolean;
};

const BaseInstallInterstitial = ({
  isOpen: isOpenAttempt = false,
  onRequestClose,
  promptLocation,
  displayTrigger,
  children,
  hidePageScrollbarGutter = false,
  isDeviceHeight = false
}: BaseInstallInterstitialProps) => {
  const experiments = useExperiments();
  const isAppsFlyerEnabled = experiments['web-appsflyer-migration']?.value === 'on';
  const isMobile = useIsMobileSizedScreen();
  const [isMounted, setIsMounted] = useState(false);
  const isOpen = isMounted && isOpenAttempt && isMobile;
  const sendExposure = useExposureEvent('web-appsflyer-migration');

  useEffect(() => {
    if (isOpen && isMobile) {
      sendExposure();
    }
  }, [isOpen, isMobile, sendExposure]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      logInstallPromptViewed({
        install_prompt_location: promptLocation,
        install_prompt_type: 'interstitial',
        install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native',
        signup_modal_trigger: displayTrigger
      });
    }
  }, [displayTrigger, isAppsFlyerEnabled, isOpen, promptLocation]);

  const handleCloseClick = () => {
    logInstallPromptDismissed({
      install_prompt_location: promptLocation,
      install_prompt_type: 'interstitial',
      install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native'
    });
    onRequestClose?.();
  };

  return (
    <Modal
      contentsClassName={styles.contents}
      hidePageScrollbarGutter={hidePageScrollbarGutter}
      isDeviceHeight={isDeviceHeight}
      isOpen={isOpen}
      onCloseRequest={handleCloseClick}
    >
      {children}
    </Modal>
  );
};

export default BaseInstallInterstitial;
